import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { emptyArray } from 'ui-components/default-props/empty';
import { NotificationService } from 'ui-components/services/notification-service';

import { loadSerialNumbersForFulfillmentPlan } from './serial-number.api';
import { messages } from './messages';

import type { SerialNumber } from './serial-number.api';

type SerialNumberContextValue = {
  fulfillmentPlanId?: number;
  getSerialNumbers: (fulfillmentPlanItemId: number) => readonly SerialNumber[];
  refresh: () => void;
};

const SerialNumberContext = createContext<SerialNumberContextValue>({
  fulfillmentPlanId: undefined,
  getSerialNumbers: () => emptyArray,
  refresh: () => undefined,
});

export const useSerialNumberContext = (): SerialNumberContextValue => useContext(SerialNumberContext);

type SerialNumberProviderProps = {
  children: ReactNode;
  fulfillmentPlanId?: string;
  isEnabled: boolean;
};

// Batch-loads all serial numbers for one fulfillment plan once (one request per
// drawer open), instead of one request per item row. The result map is keyed by
// fulfillmentPlanItemId (the item-level id), which the backend exposes as
// orderItemId (= dbo.OrderItem.OrderItemID) on the response — distinct from the
// plan-level id used to fetch the batch. After a save, rows call refresh() to
// reload the batch — matching how the drawer reloads data after a mutation rather
// than merging in place.
export const SerialNumberProvider = ({ children, fulfillmentPlanId, isEnabled }: SerialNumberProviderProps) => {
  const [serialNumbersByFulfillmentPlanItemId, setSerialNumbersByFulfillmentPlanItemId] = useState<Map<number, SerialNumber[]>>(new Map());

  const fulfillmentPlanIdNumberRaw = fulfillmentPlanId ? Number(fulfillmentPlanId) : undefined;
  const fulfillmentPlanIdNumber =
    fulfillmentPlanIdNumberRaw != null && !Number.isNaN(fulfillmentPlanIdNumberRaw) ? fulfillmentPlanIdNumberRaw : undefined;

  // Tracks mount status so neither the initial load nor a child-triggered refresh()
  // calls setState after the provider unmounts (e.g. the drawer closes mid-request).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!isEnabled || !fulfillmentPlanIdNumber) {
      return;
    }
    loadSerialNumbersForFulfillmentPlan(fulfillmentPlanIdNumber)
      .then((items) => {
        if (isMountedRef.current) {
          setSerialNumbersByFulfillmentPlanItemId(new Map(items.map((item) => [item.orderItemId, item.serialNumbers])));
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setSerialNumbersByFulfillmentPlanItemId(new Map());
          NotificationService.showGenericFailureNotification(<FormattedMessage {...messages.loadError} />);
        }
      });
  }, [isEnabled, fulfillmentPlanIdNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo<SerialNumberContextValue>(
    () => ({
      fulfillmentPlanId: fulfillmentPlanIdNumber,
      getSerialNumbers: (fulfillmentPlanItemId: number) => serialNumbersByFulfillmentPlanItemId.get(fulfillmentPlanItemId) ?? emptyArray,
      refresh: load,
    }),
    [fulfillmentPlanIdNumber, serialNumbersByFulfillmentPlanItemId, load],
  );

  return <SerialNumberContext.Provider value={value}>{children}</SerialNumberContext.Provider>;
};
